import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { Skeleton } from "@material-ui/lab";
import * as React from "react";

import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";
import { getMonthlyRentByProperty, isPropertyCurrentInUse } from "@/utilities/calculateProperty";
import { getPropertyAddressString } from "@/utilities/getPropertyAddressString";
import { formatYieldRateForDisplay } from "@/utilities/yield";
import type { PropertyHolder } from "@/view_models/atoms";

import NoImage from "@assets/image/NoImage.svg";
import PropertySampleImage from "@assets/image/property_sample_image01.png";

import { CustomButton } from "../Button";
import { ThinArrowIcon } from "../icon/ThinArrowIcon";

const useStyles = makeStyles({
  root: {
    padding: layout.space[16],
    paddingLeft: layout.space[24],
    paddingRight: layout.space[24],
    [theme.breakpoints.up("md")]: { padding: "32px 0" },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: layout.space[24],
    marginBottom: layout.space[24],
  },
  title: { ...textStyles["PC/Heading/H2"], color: colors["text/primary"] },
  count: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    marginBottom: layout.space[16],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"], marginTop: layout.space[12], marginBottom: 0 },
  },
  add: { width: 240, flexShrink: 0 },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: layout.space[16],
    [theme.breakpoints.up("md")]: {
      background: colors["surface/card"],
      borderRadius: layout.radius.lg,
      gap: 0,
      overflow: "hidden",
    },
  },
  tableHead: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 160px 160px 120px 16px",
    gap: layout.space[24],
    padding: "12px 24px",
    borderBottom: `1px solid ${colors["border/default"]}`,
    color: colors["text/secondary"],
    ...textStyles["PC/Body/Small"],
    "& span:not(:first-child)": { textAlign: "right" },
    "@media (max-width: 1199px)": {
      gridTemplateColumns: "minmax(0, 1fr) 88px 100px 72px 16px",
      gap: layout.space[16],
    },
  },
  listItem: {
    [theme.breakpoints.up("md")]: {
      borderBottom: `1px solid ${colors["border/default"]}`,
      "&:last-child": { borderBottom: 0 },
    },
  },
  row: {
    width: "100%",
    textAlign: "left",
    display: "flex",
    gap: layout.space[12],
    padding: layout.space[16],
    alignItems: "flex-start",
    color: colors["text/primary"],
    background: colors["surface/card"],
    borderRadius: layout.radius.lg,
    boxShadow: `0 2px 8px ${alpha(colors["text/primary"], 0.06)}`,
    [theme.breakpoints.up("md")]: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 160px 160px 120px 16px",
      gap: layout.space[24],
      alignItems: "center",
      padding: "16px 24px",
      borderRadius: 0,
      boxShadow: "none",
      borderBottom: `1px solid ${colors["border/default"]}`,
      "&:last-child": { borderBottom: 0 },
      "@media (max-width: 1199px)": {
        gridTemplateColumns: "minmax(0, 1fr) 88px 100px 72px 16px",
        gap: layout.space[16],
      },
    },
    "&:hover": { backgroundColor: colors["surface/subtle"] },
    "&:focus-visible": { outline: `2px solid ${colors["border/focus"]}`, outlineOffset: -2 },
  },
  identity: {
    display: "flex",
    alignItems: "center",
    gap: layout.space[16],
    minWidth: 0,
    [theme.breakpoints.down("sm")]: { display: "contents" },
  },
  photo: {
    width: 96,
    height: 96,
    objectFit: "cover",
    borderRadius: layout.radius.md,
    flexShrink: 0,
    [theme.breakpoints.up("md")]: { height: 72 },
  },
  texts: {
    minWidth: 0,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[8],
    [theme.breakpoints.up("md")]: { gap: layout.space[4] },
  },
  nameRow: { display: "flex", alignItems: "center", gap: layout.space[8], minWidth: 0 },
  name: {
    ...textStyles["SP/Heading/H4"],
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Heading/H4"] },
  },
  address: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
  badge: {
    ...textStyles["SP/Heading/H5"],
    color: colors["status/success"],
    background: colors["status/success-bg"],
    borderRadius: layout.radius.full,
    padding: "4px 8px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Heading/H5"] },
  },
  vacant: { color: colors["text/secondary"], background: colors["surface/base"] },
  metrics: { display: "flex", gap: layout.space[16], flexWrap: "wrap" },
  label: { ...textStyles["SP/Body/Small"], color: colors["text/secondary"], marginBottom: layout.space[4] },
  amount: {
    ...textStyles["Number/XS"],
    whiteSpace: "nowrap",
    [theme.breakpoints.up("md")]: { ...textStyles["Number/S"], textAlign: "right", fontFeatureSettings: "\"tnum\"" },
  },
  unit: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    marginLeft: 2,
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
  arrow: { width: 16, height: 16, color: colors["icon/default"] },
});

type Props = {
  properties: PropertyHolder[];
  consideration?: boolean;
  count: number;
  getActualYield: (property: PropertyHolder) => string;
  onSelect: (id: number) => void;
  onAdd: () => void;
  loading?: boolean;
  children?: React.ReactNode;
};

export const ManagedPropertyList: React.FC<Props> = ({
  properties,
  consideration,
  count,
  getActualYield,
  onSelect,
  onAdd,
  loading,
  children,
}) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const rentLabel = consideration ? "想定家賃（月）" : "家賃（月）";
  return (
    <Box className={classes.root}>
      {!isMobile ? (
        <Box className={classes.header}>
          <Box>
            <Typography component="h1" className={classes.title}>
              {consideration ? "検討中物件" : "保有物件"}
            </Typography>
            <Typography className={classes.count}>{loading ? "読み込み中" : `${count}件を管理中`}</Typography>
          </Box>
          <Box className={classes.add}>
            <CustomButton buttonSize="M" fullWidth onClick={onAdd}>
              物件を登録する
            </CustomButton>
          </Box>
        </Box>
      ) : (
        <Typography className={classes.count}>{loading ? "読み込み中" : `${count}件を管理中`}</Typography>
      )}
      {children}
      <Box
        className={classes.list}
        role="list"
        aria-label={consideration ? "検討中物件一覧" : "保有物件一覧"}
        aria-busy={loading}
      >
        {!isMobile && (
          <Box className={classes.tableHead} aria-hidden="true">
            <span />
            <span>{consideration ? "販売価格" : "購入価格"}</span>
            <span>{rentLabel}</span>
            <span>実質利回り</span>
            <span />
          </Box>
        )}
        {loading
          ? [0, 1, 2].map((id) => (
              <Box key={id} className={classes.row}>
                <Skeleton variant="rect" width={96} height={isMobile ? 96 : 72} />
                <Box flex={1}>
                  <Skeleton width="80%" />
                  <Skeleton width="60%" />
                </Box>
              </Box>
            ))
          : properties.map((holder) => {
              const active = isPropertyCurrentInUse(holder.lease_contracts);
              const rent = Math.round(getMonthlyRentByProperty(holder)).toLocaleString();
              const yieldRate = formatYieldRateForDisplay(getActualYield(holder));
              return (
                <Box key={holder.id} role="listitem" className={classes.listItem}>
                  <ButtonBase
                    className={classes.row}
                    onClick={() => onSelect(holder.property.id)}
                    aria-label={`${holder.property.name}の詳細`}
                  >
                    <Box className={classes.identity}>
                      <img
                        className={classes.photo}
                        src={
                          holder.cover_image_url && holder.cover_image_url !== NoImage
                            ? holder.cover_image_url
                            : PropertySampleImage
                        }
                        alt=""
                      />
                      <Box className={classes.texts}>
                        <Box className={classes.nameRow}>
                          <Typography className={classes.name}>{holder.property.name}</Typography>
                          {!consideration && holder.property.property_category !== "一棟物件" && (
                            <span className={`${classes.badge} ${active ? "" : classes.vacant}`}>
                              {active ? "入居中" : "空室中"}
                            </span>
                          )}
                        </Box>
                        <Typography className={classes.address}>{getPropertyAddressString(holder)}</Typography>
                        {isMobile && (
                          <Box className={classes.metrics}>
                            <Box>
                              <Typography className={classes.label}>{rentLabel}</Typography>
                              <span className={classes.amount}>
                                {rent}
                                <span className={classes.unit}>円</span>
                              </span>
                            </Box>
                            <Box>
                              <Typography className={classes.label}>実質利回り</Typography>
                              <span className={classes.amount}>{yieldRate}</span>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {!isMobile && (
                      <>
                        <span className={classes.amount}>
                          {Math.round(holder.property.total_price_of_property / 10000).toLocaleString()}
                          <span className={classes.unit}>万円</span>
                        </span>
                        <span className={classes.amount}>
                          {rent}
                          <span className={classes.unit}>円</span>
                        </span>
                        <span className={classes.amount}>{yieldRate}</span>
                        <ThinArrowIcon className={classes.arrow} />
                      </>
                    )}
                  </ButtonBase>
                </Box>
              );
            })}
      </Box>
    </Box>
  );
};
